import { getEnvironment } from "@p0security/frontend-core";
import { getEnvProjectId } from "@p0security/frontend-core/react";
import {
  GCLOUD_CLOUD_RUN_DEFAULT_REGION,
  GCLOUD_COMPONENT_SETUP,
  GCLOUD_SECURITY_PERIMETER_SETUP_IMAGES,
  serviceAccountForSecurityPerimeter,
} from "@p0security/integrations-gcloud-shared/constants";
import type {
  GcloudIntegration,
  GcloudSecurityPerimeterComponent,
  GcloudSecurityPerimeterComponentKey,
} from "@p0security/integrations-gcloud-shared/types";
import {
  installedSecurityPerimeter,
  rootConfig,
} from "@p0security/integrations-gcloud-shared/util";
import type { FrontendInstallContext } from "@p0security/integrations-installer";
import { installedItems } from "@p0security/shared/install/installed";
import type { Instructions } from "@p0security/shared/install/types";
import { Typography } from "@p0security/ui";
import { compact, entries, keys } from "lodash";
import type { ReactNode } from "react";

import { CommandDisplay } from "../CommandDisplay.tsx";
import type { GcloudInstaller } from "./types";

const { Text, Paragraph } = Typography;

/**
 * Generates Terraform for granting the target project's security perimeter
 * service account the permissions needed to manage IAM in that project.
 */
export const securityPerimeterSaTerraform = (
  context: FrontendInstallContext<GcloudIntegration>,
  data: (typeof GCLOUD_COMPONENT_SETUP)["iam-write-2"]["securityPerimeter"],
  targetProjectId: string
) => {
  const { projectId: securityPerimeterProjectId } = installedSecurityPerimeter(
    context.config
  );
  const { predefinedRole, customRole, requiredPermissions } = data;
  const { email: p0securityPerimeterServiceAccount } =
    serviceAccountForSecurityPerimeter(securityPerimeterProjectId);

  return `# Creates security perimeter custom role
resource "google_project_iam_custom_role" "${customRole.id}-${targetProjectId}" {
  role_id      = "${customRole.id}"
  project      = "${targetProjectId}"

  description  = "Role used by security perimeter service account to manage IAM in ${targetProjectId}"
  stage        = "GA"
  title        = "${customRole.name}"

  permissions = [
    ${requiredPermissions.map((p) => `"${p}"`).join(",\n    ")}
  ]
}

# Gives the p0 security perimeter service account access to the role just created
resource "google_project_iam_member" "${customRole.id}-${targetProjectId}-membership" {
  role         = "projects/${targetProjectId}/roles/${customRole.id}"
  project      = "${targetProjectId}"
  member       = "serviceAccount:${p0securityPerimeterServiceAccount}"
}

# Provides predefined role access to the p0 security perimeter service account
resource "google_project_iam_member" "security-perimeter-${targetProjectId}-predefined-membership" {
  role         = "${predefinedRole}"
  project      = "${targetProjectId}"
  member       = "serviceAccount:${p0securityPerimeterServiceAccount}"
}
  `;
};

/**
 * Generates instructions for granting permissions to the security perimeter
 * service account to manage iam in the target project
 * @param data security perimeter setup data
 * @param targetProjectId target project id
 * @returns
 */
export const securityPerimeterSaInstructions = (
  context: FrontendInstallContext<GcloudIntegration>,
  data: (typeof GCLOUD_COMPONENT_SETUP)["iam-write-2"]["securityPerimeter"],
  targetProjectId: string
) => {
  const { projectId: securityPerimeterProjectId } = installedSecurityPerimeter(
    context.config
  );
  const { predefinedRole, customRole, requiredPermissions } = data;
  const { email: p0securityPerimeterServiceAccount } =
    serviceAccountForSecurityPerimeter(securityPerimeterProjectId);
  return `# creates security perimeter custom role
gcloud iam roles create ${customRole.id} \\
  --stage=GA \\
  --project=${targetProjectId} \\
  --title="${customRole.name}" \\
  --description="Role used by security perimeter service account to manage IAM in ${targetProjectId}"

# updates permissions for the security perimeter custom role
gcloud iam roles update ${customRole.id} \\
  --project=${targetProjectId} \\
  --permissions=${requiredPermissions.join(",\\\n")}
  
# gives the p0 security perimeter service account access to the role just created
gcloud projects add-iam-policy-binding ${targetProjectId} \\
  --member="serviceAccount:${p0securityPerimeterServiceAccount}" \\
  --condition=None \\
  --role="projects/${targetProjectId}/roles/${customRole.id}"
  
# provides predefine role access to the p0 security perimeter service account 
gcloud projects add-iam-policy-binding ${targetProjectId}  \\
  --member="serviceAccount:${p0securityPerimeterServiceAccount}" \\
  --condition=None \\
  --role="${predefinedRole}"
  `;
};

/**
 * These are commands for migrating old customers to security perimeter
 * @param context front end install context
 * @returns
 */
const permissionCommands = (
  context: FrontendInstallContext<GcloudIntegration>
) => ({
  "iam-write-security-perimeter": {
    itemCommands: (
      p0securityPerimeterServiceAccount: string,
      serviceAccountEmail: string
    ) => {
      const {
        customRole: readerRole,
        predefinedRole: readerPredefinedRole,
        requiredPermissions: readerRequiredPermissions,
      } = GCLOUD_COMPONENT_SETUP["iam-write-2"];
      const {
        customRole: securityPerimeterRole,
        predefinedRole: securityPerimeterPredefinedRole,
        requiredPermissions: securityPerimeterRequiredPermissions,
      } = GCLOUD_COMPONENT_SETUP["iam-write-2"]["securityPerimeter"];

      const { customRole: iamAdminRole } = GCLOUD_COMPONENT_SETUP["iam-write"];

      // TODO: remove all the iam-write specific instructions once all the clients are moved to the new security-perimeter
      const projectItems = installedItems("iam-write", context.config);
      return entries(projectItems).length
        ? `
# migrate existing installs to the new security perimeter
for project in ${keys(projectItems).join(" ")}; do
  
  writeRolePath="projects/$project/roles/${securityPerimeterRole.id}"
  adminRolePath="projects/$project/roles/${iamAdminRole.id}"
  readerRolePath="projects/$project/roles/${readerRole.id}"
  
  # creates reader role
  gcloud iam roles create ${readerRole.id} \\
    --stage=GA \\
    --project=$project \\
    --title="${readerRole.name}" \\
    --description="Role used by p0 service account to read iam in $project"
  
  # adds permissions to the reader role
  gcloud iam roles update ${readerRole.id} \\
    --project=$project \\
    --add-permissions=${readerRequiredPermissions.join(",\\\n")}

  # gives the p0 service account access to the reader role just created
  gcloud projects add-iam-policy-binding $project \\
    --member="serviceAccount:${serviceAccountEmail}" \\
    --condition=None \\
    --role="$readerRolePath"

  # provides predefined role access to the p0 service account
  gcloud projects add-iam-policy-binding $project \\
    --member="serviceAccount:${serviceAccountEmail}" \\
    --condition=None \\
    --role="${readerPredefinedRole}"

  # creates writer role
  gcloud iam roles create ${securityPerimeterRole.id} \\
    --stage=GA \\
    --project=$project \\
    --title="${securityPerimeterRole.name}" \\
    --description="Role used by p0 security perimeter service account to manage iam in $project"

  # updates permissions for the writer role
  gcloud iam roles update ${securityPerimeterRole.id}  \\
    --project=$project \\
    --permissions=${securityPerimeterRequiredPermissions.join(",\\\n")}
   
  # gives the security perimeter service account access to the writer role just created
  gcloud projects add-iam-policy-binding $project \\
    --member="serviceAccount:${p0securityPerimeterServiceAccount}" \\
    --condition=None \\
    --role="$writeRolePath"

  # provides predefine role access to the security perimeter service account
  gcloud projects add-iam-policy-binding $project \\
    --member="serviceAccount:${p0securityPerimeterServiceAccount}" \\
    --condition=None \\
    --role="${securityPerimeterPredefinedRole}"

  # removes the p0 service account from the old admin role
  gcloud projects remove-iam-policy-binding $project \\
    --member="serviceAccount:${serviceAccountEmail}" \\
    --condition=None \\
    --role="$adminRolePath"
  
  # removes the p0 service account from the predefined role from older installations
  gcloud projects remove-iam-policy-binding $project \\
    --member="serviceAccount:${serviceAccountEmail}" \\
    --condition=None \\
    --role="${securityPerimeterPredefinedRole}"


done
  `
        : undefined;
    },
  },
});

/**
 *
 * @param config
 * @returns
 */
const securityPerimeterHelperCommands = (config: {
  context: FrontendInstallContext<GcloudIntegration>;
  component: GcloudSecurityPerimeterComponentKey;
  securityPerimeterServiceAccount: string;
  serviceAccountEmail: string;
}) =>
  permissionCommands(config.context)[config.component].itemCommands(
    config.securityPerimeterServiceAccount,
    config.serviceAccountEmail
  );

const securityPerimeterCommands = (
  context: FrontendInstallContext<GcloudIntegration>,
  component: GcloudSecurityPerimeterComponentKey,
  item: {
    projectId: string;
    allowedDomains: string;
    imageDigest: string;
    region?: string;
  }
) => {
  const { cloudRunNameFor, imageName } =
    GCLOUD_SECURITY_PERIMETER_SETUP_IMAGES[component];
  const { environment } = getEnvironment();
  const envProjectId = getEnvProjectId();
  const cloudRunName = cloudRunNameFor(environment);
  const { serviceAccountEmail } = rootConfig(context.config);
  const { projectId, allowedDomains, imageDigest, region } = item;
  const cloudRunRegion = region || GCLOUD_CLOUD_RUN_DEFAULT_REGION;
  const { name: securityPerimeterName, email: securityPerimeterEmail } =
    serviceAccountForSecurityPerimeter(projectId);
  const commands = securityPerimeterHelperCommands({
    context,
    component,
    securityPerimeterServiceAccount: securityPerimeterEmail,
    serviceAccountEmail,
  });
  const { customRole, requiredPermissions, services } =
    GCLOUD_COMPONENT_SETUP["iam-write-security-perimeter"];

  const {
    customRole: readerRole,
    requiredPermissions: readerRequiredPermissions,
  } =
    GCLOUD_COMPONENT_SETUP["iam-write-security-perimeter"]["projectReaderRole"];

  return compact([
    {
      header: `Please execute the following commands ${
        !commands ? "and click 'Next'" : ""
      }:`,
      command: `# creates service account for p0 security perimeter
gcloud iam service-accounts create ${securityPerimeterName} \\
  --display-name="P0 Security Perimeter Service Account" \\
  --description="Service account to manage p0 security perimeter" \\
  --project=${projectId}

# enable iam and cloud run services
${services
  .map((service) => `gcloud services enable ${service} --project=${projectId}`)
  .join("\n")}

# deploys p0 security perimeter
# INVOKER_SA_EMAIL is the email of the service account you are using to configure P0
gcloud run deploy ${cloudRunName} --image=${imageName}:${imageDigest} \\
  --service-account=${securityPerimeterEmail} \\
  --project=${projectId} \\
  --region=${cloudRunRegion} --port=8080 \\
  --no-allow-unauthenticated \\
  --set-env-vars DOMAIN_ALLOW_PATTERN="${allowedDomains}" \\
  --set-env-vars INVOKER_SA_EMAIL=${serviceAccountEmail} \\
  --set-env-vars GCLOUD_PROJECT=${envProjectId} 
  
# creates p0 security perimeter invoker role, this is assumed by p0 service account
gcloud iam roles create ${customRole.id} \\
  --stage=GA \\
  --project=${projectId} \\
  --title="P0 security perimeter invoker" \\
  --description="${customRole.name}"
# adds permissions to the security perimeter invoker role
gcloud iam roles update ${customRole.id} \\
  --project=${projectId} \\
  --add-permissions=${requiredPermissions.join(",\\\n")}
      
# gives the p0 service account access to the role just created
gcloud run services add-iam-policy-binding ${cloudRunName} \\
  --region=${cloudRunRegion} \\
  --project=${projectId} \\
  --member="serviceAccount:${serviceAccountEmail}" \\
  --role="projects/${projectId}/roles/${customRole.id}"

# creates reader role
gcloud iam roles create ${readerRole.id} \\
  --stage=GA \\
  --project=${projectId} \\
  --title="${readerRole.name}" \\
  --description="Role used by p0 service account to read iam in ${projectId}"
  
# adds permissions to the reader role
gcloud iam roles update ${readerRole.id} \\
  --project=${projectId} \\
  --add-permissions=${readerRequiredPermissions.join(",\\\n")}

# gives the p0 service account access to the reader role just created
gcloud projects add-iam-policy-binding ${projectId} \\
  --member="serviceAccount:${serviceAccountEmail}" \\
  --condition=None \\
  --role="projects/${projectId}/roles/${readerRole.id}"`,
    },
    commands
      ? {
          header: `Please execute the following commands to grant permissions for existing installs and click 'Next'`,
          command: commands,
        }
      : undefined,
  ]);
};

const securityPerimeterTerraform = (
  context: FrontendInstallContext<GcloudIntegration>,
  component: GcloudSecurityPerimeterComponentKey,
  item: {
    projectId: string;
    allowedDomains: string;
    imageDigest: string;
    region?: string;
  }
) => {
  const { imageName } = GCLOUD_SECURITY_PERIMETER_SETUP_IMAGES[component];
  const envProjectId = getEnvProjectId();
  const { serviceAccountEmail } = rootConfig(context.config);
  const { projectId, allowedDomains, imageDigest, region } = item;
  const {
    customRole: invokerRole,
    requiredPermissions: invokerRequiredPermissions,
    services,
  } = GCLOUD_COMPONENT_SETUP["iam-write-security-perimeter"];
  const {
    customRole: readerRole,
    requiredPermissions: readerRequiredPermissions,
  } =
    GCLOUD_COMPONENT_SETUP["iam-write-security-perimeter"]["projectReaderRole"];

  return `# Enable IAM and Cloud Run services
resource "google_project_service" "enable_services" {
  project = "${projectId}"
  for_each = toset([
    ${services.map((service) => `"${service}"`).join(",\n    ")}
  ])
  service            = each.key
  disable_on_destroy = false
}

# Create P0 Security Perimeter service account
resource "google_service_account" "security_perimeter_sa" {
  project      = "${projectId}"
  account_id   = "${serviceAccountForSecurityPerimeter(projectId).name}"
  display_name = "P0 Security Perimeter Service Account"
  description  = "Service account to manage p0 security perimeter"

  depends_on = [google_project_service.enable_services]
}

# Deploys P0 Security Perimeter cloud run service
resource "google_cloud_run_service" "security_perimeter" {
  project  = "${projectId}"
  name     = "${GCLOUD_SECURITY_PERIMETER_SETUP_IMAGES[
    component
  ].cloudRunNameFor(context.environment)}"
  location = "${region || GCLOUD_CLOUD_RUN_DEFAULT_REGION}"

  template {
    spec {
      containers {
        image = "${imageName}:${imageDigest}"
        env {
          name  = "DOMAIN_ALLOW_PATTERN"
          value = ${JSON.stringify(allowedDomains)}
        }
        env {
          name  = "GCLOUD_PROJECT"
          value = "${envProjectId}"
        }
        # INVOKER_SA_EMAIL is the email of the service account you are using to configure P0
        env {
          name  = "INVOKER_SA_EMAIL"
          value = "${serviceAccountEmail}"
        }
      }
      service_account_name = google_service_account.security_perimeter_sa.email
    }
  }

  depends_on = [google_project_service.enable_services]
}

# Create the P0 Security Perimeter invoker role
resource "google_project_iam_custom_role" "invoker_role" {
  project     = "${projectId}"
  role_id     = "${invokerRole.id}"
  title       = "P0 security perimeter invoker"
  description = "Role used by P0 to invoke the security perimeter"
  permissions = [
    ${invokerRequiredPermissions.map((p) => `"${p}"`).join(",\n    ")}
  ]

  depends_on = [google_project_service.enable_services]
}

# Grants the P0 service account access to the Security Perimeter invoker role
resource "google_cloud_run_service_iam_member" "invoker_role_binding" {
  project  = "${projectId}"
  service  = google_cloud_run_service.security_perimeter.name
  location = google_cloud_run_service.security_perimeter.location
  role     = google_project_iam_custom_role.invoker_role.id
  member   = "serviceAccount:${serviceAccountEmail}"

  depends_on = [google_project_service.enable_services]
}

# Creates the P0 Security Perimeter reader role
resource "google_project_iam_custom_role" "reader_role" {
  role_id     = "${readerRole.id}"
  title       = "${readerRole.name}"
  description = "Role used by p0 service account to read IAM"
  project     = "${projectId}"
  permissions = [
    ${readerRequiredPermissions.map((p) => `"${p}"`).join(",\n    ")}
  ]

  depends_on = [google_project_service.enable_services]
}

# Grants the P0 service account access to the Security Perimeter reader role
resource "google_project_iam_member" "reader_role_binding" {
  project = "${projectId}"
  role    = google_project_iam_custom_role.reader_role.name
  member  = "serviceAccount:${serviceAccountEmail}"

  depends_on = [google_project_service.enable_services]
}`;
};

export const securityPerimeterInstructions: (
  component: GcloudSecurityPerimeterComponentKey
) => GcloudInstaller["components"]["iam-write-security-perimeter"]["instructions"] =
  (component) =>
  (context, id, item, _field): Instructions<ReactNode> => ({
    help: (
      <Paragraph>
        To install security perimeter on this account, provision P0&apos;s
        access using{" "}
        <Typography.Link
          href={`https://console.cloud.google.com/cloudshelleditor?project=${id}&cloudshell=true`}
          target="_blank"
          rel="noopener"
        >
          Cloud Shell
        </Typography.Link>
      </Paragraph>
    ),
    commands: {
      shell: [
        ...securityPerimeterCommands(context, component, {
          ...item,
          projectId: id,
        }),
      ],
      iac: [
        {
          command: securityPerimeterTerraform(context, component, {
            ...item,
            projectId: id,
          }),
        },
      ],
    },
  });

export const cloudRunUrlInstaller: GcloudInstaller["components"]["iam-write-security-perimeter"]["items"]["cloudRunUrl"] =
  {
    labeler: (
      context: FrontendInstallContext<GcloudIntegration>,
      id: string,
      item: Readonly<GcloudSecurityPerimeterComponent>
    ) => {
      const name = GCLOUD_SECURITY_PERIMETER_SETUP_IMAGES[
        "iam-write-security-perimeter"
      ].cloudRunNameFor(context.environment);
      const cloudRunRegion = item.region || GCLOUD_CLOUD_RUN_DEFAULT_REGION;
      return (
        <>
          Fetch the cloud run url from service description with{" "}
          <Text code>gcloud</Text>:
          <br />
          <CommandDisplay
            commands={`gcloud run services describe ${name} --project=${id} --platform managed --region ${cloudRunRegion} --format 'value(status.url)'`}
          />
        </>
      );
    },
  };
